import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import { FaFileContract, FaUserShield, FaArrowLeft, FaArrowRight, FaDesktop, FaMobileAlt, FaServer, FaPalette, FaRocket, FaCogs, FaIdBadge } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import toast, { Toaster } from 'react-hot-toast';

// --- Data Section ---
const custom = 'Custom';

// Service Plans with billing cycles
const servicePlans = {
  'Website Development': {
    "Starter Package": {
      price: 9999,
      billingCycle: 'one-time',
      features: ['Up to 5 static pages', 'Modern UI design', 'Responsive layout (Mobile/Desktop)', 'Basic contact form', 'Social media integration', '1 Month free support for any issues (Not Maintenance)']
    },
    "Business Package": {
      price: 24999,
      billingCycle: 'one-time',
      features: ['Up to 12 pages', 'Dynamic content', 'SEO setup - Friendly structure', 'Custom design layout', '2 Months free support for any issues (Not Maintenance)']
    },
    "E-Commerce Package": {
      price: 39999,
      billingCycle: 'one-time',
      features: ['Product listing & shopping cart', 'Payment gateway integration', 'Admin Panel (INR 5000) Included', 'Customer login/register system', '3 Months free support for any issues (Not Maintenance)']
    },
    "Enterprise Package": {
      price: custom,
      billingCycle: 'custom',
      features: ['Based on the requirements & pricing will be shared after discussion', 'Custom features as per business needs', 'Advanced security & performance optimization', 'Ongoing support & maintenance']
    },
  },
  'Web Application': {
    "Starter Package": {
      price: 14999,
      billingCycle: 'one-time',
      features: ['Up to 5 static pages', 'Modern UI design', 'Responsive layout (Mobile/Desktop)', 'Basic contact form', 'Social media integration', '1 Month free support for any issues (Not Maintenance)']
    },
    "Business Package": {
      price: 29999,
      billingCycle: 'one-time',
      features: ['Up to 12 pages', 'Dynamic content', 'SEO setup - Friendly structure', 'Custom design layout', '2 Months free support for any issues (Not Maintenance)']
    },
    "E-Commerce Package": {
      price: 34999,
      billingCycle: 'one-time',
      features: ['Product listing & shopping cart', 'Payment gateway integration', 'Admin Panel (INR 5000) Included', 'Customer login/register system', '3 Months free support for any issues (Not Maintenance)']
    },
    "Enterprise Package": {
      price: custom,
      billingCycle: 'custom',
      features: ['Based on the requirements & pricing will be shared after discussion', 'Custom features as per business needs', 'Advanced security & performance optimization', 'Ongoing support & maintenance']
    },
  },
  'App Development': {
    Basic: {
      price: 29999,
      billingCycle: 'one-time',
      features: ['Single platform (iOS or Android)', 'Basic UI - Up to 5 screens', 'User authentication system', 'Basic database integration', '2 Months free support for any issues (Not Maintenance)']
    },
    "Standard Plan": {
      price: 59999,
      billingCycle: 'one-time',
      features: ['Cross-platform (iOS & Android)', 'Advanced UI - Up to 10 screens', 'Backend API integration', 'Admin Panel (INR 5000) Included', 'Push notifications', 'Analytics integration']
    },
    "Enterprise Plan": {
      price: custom,
      billingCycle: 'custom',
      features: ['Based on the requirements & pricing will be shared after discussion', 'Custom features as per business needs', 'Advanced security & performance optimization', 'Ongoing support & maintenance']
    }
  },
  'Website Maintenance': {
    Basic: {
      price: 999, // Corrected from 9999 as it seemed like a typo compared to others
      billingCycle: 'per month',
      features: ['Up to 2 hour of updates/month', 'Monthly Backup', 'Basic security checks', 'Email support']
    },
    "Standard Plan": {
      price: 2499,
      billingCycle: 'per month',
      features: ['Up to 3 hours of updates/month', 'Weekly backup', 'Plugin & CMS updates', 'Performance optimization', 'Basic SEO monitoring', 'Priority email support']
    },
    "Premium Plan": {
      price: 4999,
      billingCycle: 'per month',
      features: ['Up to 6 hours of updates/month', 'Daily backup', 'Uptime monitoring', 'Malware scans & removal', 'Content updates (texts/images)', 'Monthly performance reports', 'WhatsApp/Call']
    },
    "Custom Plan": {
      price: custom,
      billingCycle: 'custom',
      features: ['Tailored to your needs', 'Includes all Premium features', 'Dedicated support', 'Custom SLA options']
    }
  },
  'Website Designing': {
    Basic: {
      price: 4999,
      billingCycle: 'one-time',
      features: ['Home + About + Contact pages', 'Responsive layout (Mobile/Desktop)', 'Modern UI', '1-Week turnaround']
    },
    "Business Plan": {
      price: 11999,
      billingCycle: 'one-time',
      features: ['Up to 8 designed pages', 'Interactive UI/UX with animation', 'Figma or Adobe XD design files', 'Mobile-first design']
    },
    "Advanced UI/UX Design": {
      price: 19999,
      billingCycle: 'one-time',
      features: ['High-end design system', 'Prototyping and wireframing', 'User journey mapping', 'Custom illustrations/icons']
    },
  },
  'Digital Portfolio Development': {
    "Silver Package": {
      price: 4999,
      billingCycle: 'one-time',
      features: ['Up to 5 Slides', 'Modern UI design', 'Responsive layout (Mobile/Desktop)', 'Basic contact form', 'Social media integration', '1 Month free support for any issues (Not Maintenance)']
    },
    "Gold Package": {
      price: 9999,
      billingCycle: 'one-time',
      features: ['Up to 10 Slides', 'Dynamic content', 'SEO setup - Friendly structure', 'Custom design layout', '2 Months free support for any issues (Not Maintenance)']
    },
  },
};

const extraFeatures = {
  'Admin Panel': { price: 5000, billingCycle: 'one-time', note: '' },
  'SEO Optimization': { price: 3000, billingCycle: 'one-time', note: 'Recommended for ranking' },
  'Domain & Hosting': { price: 1999, billingCycle: 'per year', note: 'Renewable annually. First year included in some packages.' },
  'Blog/News': { price: 3999, billingCycle: 'one-time', note: 'Price may vary based on content complexity.' },
  'WhatsApp Chat Integration': { price: 999, billingCycle: 'one-time', note: '' },
  'Multi-Language Support': { price: 2999, billingCycle: 'one-time', note: '' },
  'One-time bug fix': { price: 1499, billingCycle: 'one-time', note: '' },
  'Speed Optimization': { price: 1999, billingCycle: 'one-time', note: '' },
  'SEO Audit': { price: 1999, billingCycle: 'one-time', note: '' },
  'New page design starts': { price: 999, billingCycle: 'one-time', note: 'Per additional page' },
  'App store/Playstore publishing': { price: 6999, billingCycle: 'annually', note: '' },
  'Payment Gateway Integration': { price: 2499, billingCycle: 'one-time', note: '' },
  'Chat/Messaging module': { price: 1999, billingCycle: 'one-time', note: 'For Help/Support Page.' },
  'Style guide documentation': { price: 1999, billingCycle: 'one-time', note: '' },
};

const otherFeaturesPricing = {
  'Poster Making': { price: 200, billingCycle: 'one-time', note: '' },
  'Logo Making': { price: 500, billingCycle: 'one-time', note: '' },
  'Logo Enhancement': { price: 250, billingCycle: 'one-time', note: '' },
  'Branding': { price: 4000, billingCycle: 'one-time', note: '' },
};
// --- End of Data Section ---


// Helper component for animated steps
const Step = ({ children, direction }) => {
  const variants = {
    enter: (direction) => ({ x: direction > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction < 0 ? '100%' : '-100%', opacity: 0 }),
  };
  return (
    <motion.div
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      custom={direction}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      {children}
    </motion.div>
  );
};


const GetQuoteForm = () => {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quotationFor: '',
    services: [],
    selectedPlans: {},
    features: []
  });

  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalSection, setLegalSection] = useState('privacy');

  // --- Core Logic & Handlers ---
  
  const handleNext = () => {
    setDirection(1);
    setStep(prev => prev + 1);
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => prev - 1);
  };

  const handleServiceToggle = (svc) => {
    const isSelected = formData.services.includes(svc);
    const newServices = isSelected ? formData.services.filter(s => s !== svc) : [...formData.services, svc];
    const newSelectedPlans = { ...formData.selectedPlans };
    if (isSelected) {
      delete newSelectedPlans[svc];
    }
    setFormData({ ...formData, services: newServices, selectedPlans: newSelectedPlans });
  };

  const handleFeatureToggle = (feature) => {
    const updated = formData.features.includes(feature)
      ? formData.features.filter((v) => v !== feature)
      : [...formData.features, feature];
    setFormData({ ...formData, features: updated });
  };
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePlanSelect = (service, plan) => {
    setFormData({
      ...formData,
      selectedPlans: { ...formData.selectedPlans, [service]: plan }
    });
  };

  const quoteDetails = useMemo(() => {
    const base = formData.services.reduce((acc, service) => {
      const plan = formData.selectedPlans[service];
      const selectedPlan = plan ? servicePlans[service][plan] : null;
      return acc + (selectedPlan && selectedPlan.price !== custom ? selectedPlan.price : 0);
    }, 0);

    const extras = formData.features.reduce((acc, f) => acc + (extraFeatures[f]?.price || otherFeaturesPricing[f]?.price || 0), 0);
    const subtotal = base + extras;
    const gst = Math.round(subtotal * 0.18);
    const grandTotal = subtotal + gst;
    return { subtotal, gst, grandTotal };
  }, [formData]);

  // --- Email Generation Logic (Preserved from your original code) ---
  
  const buildPricingBreakdown = () => {
    let breakdown = '';
    formData.services.forEach((service) => {
      const plan = formData.selectedPlans[service];
      if (plan) {
        const planData = servicePlans[service][plan];
        const isCustom = planData.price === custom;
        const billingInfo = planData.billingCycle ? (planData.billingCycle === 'per month' ? ' (Per Month)' : (planData.billingCycle === 'custom' ? ' (Custom)' : '')) : '';
        breakdown += `<div style="margin-bottom:12px;"><strong style="color:#000; font-size:15px;">${service} (${plan}${isCustom ? ' - Custom Pricing' : ` - INR ${planData.price.toLocaleString()}`}${billingInfo})</strong><ul style="margin:6px 0 12px 20px; padding:0;">${planData.features.map((f) => `<li style="font-size:13px; line-height:1.4;">${f}</li>`).join('')}</ul></div>`;
      }
    });
    if (formData.features.length > 0) {
      breakdown += `<div style="margin-top:18px;"><h4 style="font-size: 15px; color: #4c1d95; margin-bottom: 8px;">Extra & Other Features Selected</h4><ul style="margin:6px 0 12px 20px; padding:0;">${formData.features.map((f) => { const ef = extraFeatures[f]; const of = otherFeaturesPricing[f]; const price = ef?.price || of?.price || 0; const note = ef?.note || of?.note || ''; const billingCycle = ef?.billingCycle || of?.billingCycle || ''; const isPerMonth = billingCycle === 'per month' ? ' (Per Month)' : ''; return `<li style="font-size:13px;">${f} - INR ${price.toLocaleString()}${isPerMonth}${note ? ` - ${note}` : ''}</li>`; }).join('')}</ul></div>`;
    }
    const { subtotal, gst, grandTotal } = quoteDetails;
    breakdown += `<div style="margin-top:18px;"><table style="width:100%; font-size:15px; border-collapse:collapse;"><tr><td style="padding:6px; border:1px solid #e5e7eb;">Subtotal</td><td style="padding:6px; border:1px solid #e5e7eb; text-align:right;">INR ${subtotal.toLocaleString()}</td></tr><tr><td style="padding:6px; border:1px solid #e5e7eb;">GST (18%)</td><td style="padding:6px; border:1px solid #e5e7eb; text-align:right;">INR ${gst.toLocaleString()}</td></tr><tr><td style="padding:6px; border:1px solid #e5e7eb; font-weight:bold;">Grand Total</td><td style="padding:6px; border:1px solid #e5e7eb; text-align:right; font-weight:bold; color:#1E40AF;">INR ${grandTotal.toLocaleString()}</td></tr></table></div>`;
    return breakdown;
  };

  const buildExtraFeaturesBreakdown = () => {
    if (formData.features.length === 0) return '<p style="font-size:13px;">None</p>';
    return `<ul style="margin:6px 0 12px 20px; padding:0;">${formData.features.map((f) => { const ef = extraFeatures[f]; const of = otherFeaturesPricing[f]; const price = ef?.price || of?.price || 0; const note = ef?.note || of?.note || ''; const isPerMonth = (ef?.billingCycle || of?.billingCycle) === 'per month' ? ' (Per Month)' : ''; return `<li style="font-size:13px;">${f} - INR ${price.toLocaleString()}${isPerMonth}${note ? ` - ${note}` : ''}</li>`; }).join('')}</ul>`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };
  
  const handleModalCancel = () => setShowConfirmModal(false);

  const handleModalConfirm = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    const { subtotal, gst, grandTotal } = quoteDetails;

    try {
      await emailjs.send(
        'service_0bg5pph', // Your Service ID
        'template_2nt2ia8', // Your Template ID
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          quotationFor: formData.quotationFor,
          pricing_breakdown: buildPricingBreakdown(),
          extra_features: buildExtraFeaturesBreakdown(),
          subtotal: `INR ${subtotal.toLocaleString()}`,
          gst: `INR ${gst.toLocaleString()}`,
          grand_total: `INR ${grandTotal.toLocaleString()}`,
        },
        'DbAR-XNICE6Uwqeck' // Your Public Key
      );
      toast.success(`Quotation sent to ${formData.email}. Our team will contact you shortly!`);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      console.error('EmailJS Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const serviceIcons = {
    'Website Development': <FaDesktop />,
    'Web Application': <FaRocket />,
    'App Development': <FaMobileAlt />,
    'Website Maintenance': <FaServer />,
    'Website Designing': <FaPalette />,
    'Digital Portfolio Development': <FaIdBadge />
  };

  // --- JSX / Rendering ---

  return (
    <section id="quote" className="bg-[#0A0A23] py-24 px-4 sm:px-8 scroll-mt-24 snap-start">
      <Toaster position="top-right" />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <motion.h2 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-blue-600">
            Build Your Quote
          </motion.h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Follow the steps to get an instant, personalized estimate for your project.</p>
        </div>

        <div className="w-full bg-gray-700 rounded-full h-2.5 mb-12">
            <div className="bg-gradient-to-r from-purple-600 to-blue-500 h-2.5 rounded-full" style={{ width: `${(step / 5) * 100}%`, transition: 'width 0.5s ease-in-out' }}></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Form Steps */}
          <div className="lg:w-2/3">
            <div className="bg-gray-800/20 p-6 sm:p-8 rounded-2xl border border-gray-700 min-h-[450px] flex items-center overflow-hidden relative">
              <AnimatePresence mode="wait" custom={direction}>
                <Step key={step} direction={direction}>
                  {step === 1 && (
                    <div>
                      <h3 className="text-2xl font-bold mb-6">Step 1: Contact Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" className="p-3 rounded-xl bg-gray-900 border border-gray-600 w-full" required />
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="p-3 rounded-xl bg-gray-900 border border-gray-600 w-full" required />
                        <input type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className="p-3 rounded-xl bg-gray-900 border border-gray-600 w-full sm:col-span-2" required />
                        <input type="text" name="quotationFor" value={formData.quotationFor} onChange={handleChange} placeholder="Quotation For (Company/Organization/Self)" className="p-3 rounded-xl bg-gray-900 border border-gray-600 w-full sm:col-span-2" required />
                      </div>
                    </div>
                  )}
                  {step === 2 && (
                    <div>
                      <h3 className="text-2xl font-bold mb-6">Step 2: What services do you need?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.keys(servicePlans).map(svc => (
                          <div key={svc} onClick={() => handleServiceToggle(svc)} className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 flex items-center gap-4 ${formData.services.includes(svc) ? 'bg-purple-600/30 border-purple-500' : 'bg-gray-900/50 border-gray-700 hover:border-purple-500'}`}>
                            <span className="text-2xl text-purple-400">{serviceIcons[svc]}</span>
                            <span className="font-semibold">{svc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {step === 3 && (
                     <div>
                      <h3 className="text-2xl font-bold mb-6">Step 3: Configure Your Plan(s)</h3>
                      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                        {formData.services.length > 0 ? formData.services.map(service => (
                          <div key={service}>
                            <h4 className="font-bold text-xl mb-3 text-purple-300 flex items-center gap-2">{serviceIcons[service]} {service}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(servicePlans[service]).map(([plan, planData]) => (
                                    <div key={plan} onClick={() => handlePlanSelect(service, plan)} className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${formData.selectedPlans[service] === plan ? 'bg-blue-600/30 border-blue-500' : 'bg-gray-900/50 border-gray-700 hover:border-blue-500'}`}>
                                        <p className="font-bold">{plan} {planData.billingCycle === 'per month' ? '(Monthly)' : ''}</p>
                                        <p className="text-lg font-semibold text-blue-300 my-1">{planData.price === custom ? 'Custom' : `₹${planData.price.toLocaleString()}`}</p>
                                        <ul className="text-xs text-gray-400 list-disc list-inside">
                                            {planData.features.slice(0, 2).map((f, i) => <li key={i}>{f}</li>)}
                                            {planData.features.length > 2 && <li>...and more</li>}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                          </div>
                        )) : <p className='text-gray-400 text-center'>Please go back and select at least one service.</p>}
                      </div>
                    </div>
                  )}
                  {step === 4 && (
                     <div>
                      <h3 className="text-2xl font-bold mb-6">Step 4: Select Add-ons & Extras</h3>
                      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                          <div>
                            <h4 className="font-bold text-xl mb-3 text-purple-300">Extra Features</h4>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                               {Object.keys(extraFeatures).map(f => (
                                   <label key={f} className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${formData.features.includes(f) ? 'bg-purple-600/30' : 'bg-gray-900/50'}`}>
                                        <input type="checkbox" checked={formData.features.includes(f)} onChange={() => handleFeatureToggle(f)} className="form-checkbox h-5 w-5 bg-gray-800 border-gray-600 text-purple-500 focus:ring-purple-600" />
                                        <div>
                                          <span className="font-semibold">{f} {extraFeatures[f].billingCycle !== 'one-time' ? `(${extraFeatures[f].billingCycle})` : ''}</span>
                                          {extraFeatures[f].note && <p className="text-xs text-gray-400">{extraFeatures[f].note}</p>}
                                        </div>
                                   </label>
                               ))}
                             </div>
                          </div>
                           <div>
                            <h4 className="font-bold text-xl mt-4 mb-3 text-purple-300">Other Features</h4>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                               {Object.keys(otherFeaturesPricing).map(f => (
                                   <label key={f} className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${formData.features.includes(f) ? 'bg-purple-600/30' : 'bg-gray-900/50'}`}>
                                        <input type="checkbox" checked={formData.features.includes(f)} onChange={() => handleFeatureToggle(f)} className="form-checkbox h-5 w-5 bg-gray-800 border-gray-600 text-purple-500 focus:ring-purple-600" />
                                        <span className="font-semibold">{f}</span>
                                   </label>
                               ))}
                             </div>
                          </div>
                      </div>
                    </div>
                  )}
                   {step === 5 && (
                     <div>
                        <h3 className="text-2xl font-bold mb-2">Step 5: Review & Confirm</h3>
                        <p className="text-gray-400 mb-6">Please review your selections. The final quote will be sent to <strong>{formData.email}</strong>.</p>
                        <div className="text-left text-gray-300 bg-gray-900/50 p-4 rounded-lg">
                           <p><strong>Name:</strong> {formData.name}</p>
                           <p><strong>Quotation For:</strong> {formData.quotationFor}</p>
                           <p className="mt-4"><strong>Total Estimate:</strong> <span className="font-bold text-2xl text-blue-400">₹{quoteDetails.grandTotal.toLocaleString()}</span> (incl. 18% GST)</p>
                           <p className="mt-6 text-sm text-gray-400">By generating a quote, you agree to our Terms of Service & Privacy Policy, Which you can review once after clicking confirm & generate quote .</p>
                        </div>
                    </div>
                  )}
                </Step>
              </AnimatePresence>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button type="button" onClick={handleBack} disabled={step === 1} className="flex items-center gap-2 bg-gray-600 text-white px-6 py-2 rounded-xl font-semibold hover:bg-gray-500 transition disabled:opacity-50 disabled:cursor-not-allowed">
                <FaArrowLeft />
              </button>
              {step < 5 && (
                <button type="button" onClick={handleNext} className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded-xl font-semibold hover:shadow-xl transition">
                  Next <FaArrowRight />
                </button>
              )}
              {step === 5 && (
                 <button type="button" onClick={handleSubmit} disabled={loading} className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-xl transition disabled:opacity-60">
                    {loading ? 'Sending...' : 'Confirm & Generate Quote'}
                </button>
              )}
            </div>
          </div>

          {/* Live Quote Sidebar */}
          <div className="lg:w-1/3">
             <div className="sticky top-24">
                <div className="bg-gray-800/20 p-6 rounded-2xl border border-gray-700">
                    <h3 className="text-2xl font-bold mb-4 border-b border-gray-600 pb-3 flex items-center gap-3"><FaCogs /> Your Quote Summary</h3>
                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 text-sm">
                        {formData.services.length === 0 && <p className="text-gray-400">Your selections will appear here.</p>}
                        {formData.services.map(svc => {
                            const planName = formData.selectedPlans[svc];
                            const planData = planName ? servicePlans[svc][planName] : null;
                            return (
                                <div key={svc}>
                                    <p className="font-semibold text-purple-300">{svc}</p>
                                    {planData && (
                                        <div className="flex justify-between items-center text-gray-300 pl-2">
                                            <span>{planName} Plan</span>
                                            <span className="font-mono">{planData.price === custom ? 'Custom' : `₹${planData.price.toLocaleString()}`}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                         {formData.features.length > 0 && (
                            <div>
                                <p className="font-semibold text-purple-300 mt-3 border-t border-gray-700 pt-3">Add-ons</p>
                                {formData.features.map(feat => {
                                    const featureData = extraFeatures[feat] || otherFeaturesPricing[feat];
                                    return (
                                       <div key={feat} className="flex justify-between items-center text-gray-300 pl-2">
                                            <span>{feat}</span>
                                            <span className="font-mono">{`₹${featureData.price.toLocaleString()}`}</span>
                                        </div>
                                    )
                                })}
                            </div>
                         )}
                    </div>
                     <div className="mt-6 border-t-2 border-gray-600 pt-4 space-y-2">
                         <div className="flex justify-between text-gray-300"><span>Subtotal</span><span className="font-mono">₹{quoteDetails.subtotal.toLocaleString()}</span></div>
                         <div className="flex justify-between text-gray-300"><span>GST (18%)</span><span className="font-mono">₹{quoteDetails.gst.toLocaleString()}</span></div>
                         <div className="flex justify-between text-white text-xl font-bold mt-2"><span>Grand Total</span><span className="font-mono text-blue-400">₹{quoteDetails.grandTotal.toLocaleString()}</span></div>
                     </div>
                </div>
             </div>
          </div>
        </div>
      </div>
       
       {/* Your preserved Modals */}
       {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Terms of Service & Privacy Policy</h3>
                <div className="mb-6 text-gray-700 flex flex-col items-center justify-center gap-2">
                    <span>By generating a quote, you agree to our</span>
                    <div className="flex flex-row items-center justify-center gap-4">
                        <button type="button" className="text-blue-600 underline font-semibold hover:text-purple-600 transition flex items-center gap-1 bg-none border-none p-0" style={{ background: 'none', border: 'none', boxShadow: 'none' }} onClick={() => { setLegalSection('terms'); setShowLegalModal(true); }}>
                            <FaFileContract className="inline text-lg" /> Terms of Service
                        </button>
                        <span className="mx-1">and</span>
                        <button type="button" className="text-blue-600 underline font-semibold hover:text-yellow-500 transition flex items-center gap-1 bg-none border-none p-0" style={{ background: 'none', border: 'none', boxShadow: 'none' }} onClick={() => { setLegalSection('privacy'); setShowLegalModal(true); }}>
                            <FaUserShield className="inline text-lg" /> Privacy Policy
                        </button>
                    </div>
                </div>
                <div className="flex justify-center gap-4">
                    <button className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded-xl font-semibold hover:shadow-xl transition" onClick={handleModalConfirm} disabled={loading}>
                        I Agree & Generate Quote
                    </button>
                    <button className="bg-gray-300 text-gray-800 px-6 py-2 rounded-xl font-semibold hover:bg-gray-400 transition" onClick={handleModalCancel} disabled={loading}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
       )}

       <Modal isOpen={showLegalModal} onClose={() => setShowLegalModal(false)}>
  <div className="w-full max-w-4xl mx-auto px-2 sm:px-6 py-4 relative overflow-y-auto max-h-[70vh]">
          {legalSection === 'privacy' ? (
            <div>
              <h1 className="text-2xl sm:text-4xl font-extrabold mb-6 sm:mb-8 text-center bg-gradient-to-r from-yellow-400 via-blue-600 to-purple-600 bg-clip-text text-transparent drop-shadow-lg">Privacy Policy</h1>
              <p className="mb-6 sm:mb-8 text-base sm:text-lg text-white text-center">MASRIQ NextTech Solutions is committed to protecting your privacy. This policy explains how we handle your information for all our stakeholders.</p>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-blue-400">1. Privacy Policy for Users/Clients</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>We collect and process personal information (name, email, phone, company details) to provide quotations, deliver services, and support your experience.</li>
                  <li>Data is stored securely and only used for service delivery, support, and legal compliance.</li>
                  <li>We do not sell or share your data with third parties except as required by law.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-purple-400">2. Privacy Policy for Investors</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Investor information (name, contact, financial details) is handled with strict confidentiality.</li>
                  <li>Data is used solely for investment management, reporting, and regulatory compliance.</li>
                  <li>We maintain robust security measures and do not disclose investor data without explicit consent or legal requirement.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-green-400">3. Privacy Policy for Partners</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Partner data (business contacts, agreements, communications) is used to manage collaborations, joint projects, and business development.</li>
                  <li>We protect partner information and only share it as necessary for partnership activities or legal obligations.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-pink-400">4. Privacy Policy for Students/Interns</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>We collect intern/student data (name, contact, educational background) for recruitment, onboarding, and program management.</li>
                  <li>Data is kept confidential and used only for internship administration and legal compliance.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-red-400">5. Privacy Policy for Employees</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Employee data (personal, payroll, performance) is collected for HR, payroll, and legal purposes.</li>
                  <li>We ensure employee privacy and only share data with authorized personnel or as required by law.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-base sm:text-xl font-bold mb-1 sm:mb-2 text-yellow-300">General Rights</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>All individuals may request access, correction, or deletion of their data by contacting <a href="mailto:info@masriq.tech" className="text-blue-300 underline">info@masriq.tech</a>.</li>
                  <li>We comply with applicable data protection laws and strive to maintain the highest standards of privacy.</li>
                </ul>
              </div>
              <p className="mb-2 text-center text-white">For questions or concerns, please contact us at <a href="mailto:info@masriq.tech" className="text-blue-300 underline">info@masriq.tech</a>.</p>
            </div>
          ) : (
            <div>
              <h1 className="text-2xl sm:text-4xl font-extrabold mb-6 sm:mb-8 text-center bg-gradient-to-r from-purple-400 via-blue-600 to-yellow-400 bg-clip-text text-transparent drop-shadow-lg">Terms of Service</h1>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-blue-400">1. Terms for Users/Clients</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Quotations are estimates and may change based on final requirements.</li>
                  <li>Services are delivered as per the agreed scope, timeline, and payment terms.</li>
                  <li>Support and maintenance are provided as per the selected plan.</li>
                  <li>At least 25% advance to be paid. Max can be 50% of the total bill once the quote is confirmed.</li>
                  <li>Once the MVP is submitted we only accept minor changes, not major ones.</li>
                  <li>If any extra page or screen is requested upon the quotation generated, we will be charging per page (Extra Pages).</li>
                  <li>Disputes are resolved under the jurisdiction of our registered location.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-purple-400">2. Terms for Investors</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Investments are subject to due diligence, risk assessment, and regulatory compliance.</li>
                  <li>Returns, reporting, and disclosures are managed transparently.</li>
                  <li>Investor relations are governed by signed agreements and applicable laws.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-green-400">3. Terms for Partners</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Partnerships are governed by mutual agreements outlining roles, responsibilities, and deliverables.</li>
                  <li>Confidentiality and non-disclosure terms apply to all shared information.</li>
                  <li>Disputes are resolved through negotiation or legal channels as specified in agreements.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 text-pink-400">4. Terms for Students/Interns</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Internship offers are subject to eligibility and program requirements.</li>
                  <li>Interns must comply with company policies and confidentiality agreements.</li>
                  <li>Completion certificates and recommendations are provided based on performance.</li>
                  <li>Payment/Stipend will depend on the project type and the work carried by the intern.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-red-400 text-lg sm:text-2xl font-bold mb-2 sm:mb-3">5. Terms for Employees</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>Employment is governed by signed contracts, company policies, and labor laws.</li>
                  <li>Employees must adhere to code of conduct, confidentiality, and performance standards.</li>
                  <li>Termination, benefits, and grievance procedures are outlined in HR policies.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-orange-300 text-base sm:text-xl font-bold mb-1 sm:mb-2">Payments & Refunds</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>If the advance is paid, only 1 whole day will be available to cancel the project. If cancelled within 1 day, all the advance paid will be refunded.</li>
                  <li>If the project is started and you cancel it, only 25% of the advance will be returned.</li>
                  <li>If the front end is completed and you cancel the project, no amount will be refunded.</li>
                  <li>If the project is near to its completion (85% complete) and you cancel it, you will have to pay a fine of 10% of the total bill as an extra with 50% advance.</li>
                  <li>If the project is cancelled and no NDA, etc. has been signed, then the project, code, etc. belongs to MASRIQ NextTech Solutions.</li>
                </ul>
              </div>
              <div className="mb-8 sm:mb-10">
                <h2 className="text-yellow-300 text-base sm:text-xl font-bold mb-1 sm:mb-2">General Terms</h2>
                <ul className="list-disc list-inside mb-2 sm:mb-4 text-white/90">
                  <li>By using our services, investing, partnering, or joining as an intern/employee, you agree to these terms.</li>
                  <li>For questions, contact <a href="mailto:info@masriq.tech" className="text-blue-300 underline">info@masriq.tech</a>.</li>
                </ul>
              </div>
              <p className="mb-2 text-center text-white">For full details, please contact us at <a href="mailto:info@masriq.tech" className="text-blue-300 underline">info@masriq.tech</a>.</p>
            </div>
          )}
        </div>
       </Modal>
    </section>
  );
};

export default GetQuoteForm;